import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Skeleton from "@mui/material/Skeleton";

import { BoardModal } from "./components/BoardModal";
import { BoardTile, BoardTileSkeleton } from "./components/BoardTile";
import { ChampionsSection } from "./components/ChampionsSection";
import { LoadFailure } from "./components/LoadFailure";
import { TracksTab } from "./components/TracksTab";
import { useHallOfFameData } from "./useHallOfFameData";
import HallOfFameSEO from "../../components/SEO/HallOfFameSEO";
import { useCountUp } from "./useCountUp";
import { groupChampionsByDriver } from "./champions";
import { SIMULATORS } from "../../utils/simulators";
import plural from "../../utils/helpers/plural";
import { Dropdown } from "../../components/Common/Dropdown";
import {
  FilterBar,
  Grid,
  HeroSection,
  HeroStats,
  HeroText,
  HeroTitle,
  Lead,
  PageNote,
  PageWrap,
  SectionHeader,
  SectionNote,
  SectionTitle,
  Spacer,
  StatBox,
  StatLabel,
  StatValue,
  TabButton,
  TabGroup,
  skeletonSx,
} from "./styles";

function AnimatedStatValue({ value }) {
  return <StatValue>{useCountUp(value)}</StatValue>;
}

// Fixed by RecordBoardKind on the backend — six DRIVERS kinds and three TEAMS kinds — so the
// loading grid can lay out the exact number of tiles that will arrive.
const DRIVER_BOARD_COUNT = 6;
const TEAM_BOARD_COUNT = 3;

const TABS = [
  { key: "drivers", title: "Пілоти" },
  { key: "teams", title: "Команди" },
  { key: "tracks", title: "Треки" },
];
const TAB_KEYS = TABS.map((t) => t.key);

export default function HallOfFamePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [tab, setTab] = useState(() => {
    if (searchParams.get("track")) return "tracks";
    const t = searchParams.get("tab");
    return TAB_KEYS.includes(t) ? t : "drivers";
  });
  const [modalKind, setModalKind] = useState(null);
  const [car, setCar] = useState(() => searchParams.get("car") || "auto");
  const [openTrackId, setOpenTrackId] = useState(() => searchParams.get("track") || null);

  const initialSimulatorParam = searchParams.get("simulator");
  const [initialSimulatorId] = useState(() =>
    SIMULATORS.some((s) => s.id === initialSimulatorParam) ? initialSimulatorParam : undefined,
  );
  const { state, setSimulator, loadBoards, loadFullBoard, loadTracks, loadChampions } =
    useHallOfFameData(initialSimulatorId);

  const { simulatorId } = state;
  const simulatorName = SIMULATORS.find((s) => s.id === simulatorId)?.name || "";
  const simulatorOptions = SIMULATORS.map((sim) => ({ value: sim.id, label: sim.name }));
  const allSeasonsNote = simulatorName
    ? `Сумарно за всі сезони ${simulatorName}`
    : "Сумарно за всі сезони ліги";

  const carOptions = useMemo(() => {
    const cars = new Set();
    (state.tracks || []).forEach((t) => (t.configurations || []).forEach((c) => cars.add(c.car)));
    return [{ value: "auto", label: "Найшвидша" }, ...Array.from(cars).sort().map((c) => ({ value: c, label: c }))];
  }, [state.tracks]);

  // The whole records dataset is small (~375 KB), so it is loaded once per simulator rather
  // than refetched on every tab switch — see hall-of-fame-record-boards-plan.md.
  useEffect(() => {
    loadBoards("DRIVERS", simulatorId);
    loadBoards("TEAMS", simulatorId);
    loadTracks(simulatorId);
    loadChampions(simulatorId);
  }, [simulatorId, loadBoards, loadTracks, loadChampions]);

  // Mirrors the visible filters into the URL so the page can be bookmarked or shared with the
  // same tab/simulator/car/track applied.
  useEffect(() => {
    const params = { tab, simulator: simulatorId };
    if (tab === "tracks" && car !== "auto") params.car = car;
    if (tab === "tracks" && openTrackId) params.track = openTrackId;
    setSearchParams(params, { replace: true });
  }, [tab, simulatorId, car, openTrackId, setSearchParams]);

  const handleSelectDriver = (driverId) => {
    navigate(`/drivers/${driverId}?tab=statistics&simulator=${simulatorId}`);
  };

  const driverBoards = state.boardsByGroup.DRIVERS;
  const teamBoards = state.boardsByGroup.TEAMS;

  // Each dataset carries its own "not loaded yet" value, so the four sections show and clear
  // their skeletons independently instead of waiting on the slowest request.
  const championsLoading = state.champions === null;
  const driverBoardsLoading = !driverBoards;
  const teamBoardsLoading = !teamBoards;

  // A failed section shows its own way back instead of a skeleton that never resolves.
  const { errors } = state;

  // One entry per champion driver, ranked by number of titles — not one per season.
  const champions = useMemo(
    () => groupChampionsByDriver(state.champions, simulatorName),
    [state.champions, simulatorName],
  );

  const heroStats = useMemo(() => {
    const rankedDrivers = new Set();
    (driverBoards || []).forEach((board) => (board.rows || []).forEach((row) => rankedDrivers.add(row.holderId)));
    const tracksLoading = state.tracks === null;
    const seasonCount = champions.reduce((sum, c) => sum + c.titleCount, 0);
    const trackCount = state.tracks?.length || 0;
    const driverCount = rankedDrivers.size;
    return [
      // champions is grouped by driver now, so the season count is the sum of their titles.
      {
        key: "seasons",
        value: seasonCount,
        label: plural(seasonCount, ["сезон", "сезони", "сезонів"]),
        loading: championsLoading,
        failed: Boolean(state.errors.champions),
      },
      {
        key: "tracks",
        value: trackCount,
        label: `${plural(trackCount, ["трек", "треки", "треків"])} із рекордами`,
        loading: tracksLoading,
        failed: Boolean(state.errors.tracks),
      },
      {
        key: "drivers",
        value: driverCount,
        label: `${plural(driverCount, ["пілот", "пілоти", "пілотів"])}-${plural(driverCount, [
          "рекордсмен",
          "рекордсмени",
          "рекордсменів",
        ])}`,
        loading: driverBoardsLoading,
        failed: Boolean(state.errors["boards:DRIVERS"]),
      },
    ];
    // A stat box shows a bar rather than a zero counting up, which would otherwise read as a real
    // answer of nought.
  }, [champions, driverBoards, state.tracks, state.errors, championsLoading, driverBoardsLoading]);

  return (
    <PageWrap>
      <HallOfFameSEO />
      <HeroSection>
        <HeroText>
          <HeroTitle>Зала Слави</HeroTitle>
          <Lead>
            Легенди, що писали історію Slick Racing Ukraine — чемпіони, рекордсмени та пілоти, чиї найшвидші кола
            досі ніхто не побив.
          </Lead>
        </HeroText>
        <HeroStats>
          {heroStats.map((s) => (
            <StatBox key={s.key}>
              {s.failed ? (
                <StatValue>—</StatValue>
              ) : s.loading ? (
                <Skeleton variant="text" width={72} height={26} sx={skeletonSx} />
              ) : (
                <AnimatedStatValue value={s.value} />
              )}
              <StatLabel>{s.label}</StatLabel>
            </StatBox>
          ))}
        </HeroStats>
      </HeroSection>

      <FilterBar>
        <TabGroup>
          {TABS.map((t) => (
            <TabButton
              key={t.key}
              $active={tab === t.key}
              onClick={() => {
                setTab(t.key);
                if (t.key !== "tracks") setOpenTrackId(null);
              }}
            >
              {t.title}
            </TabButton>
          ))}
        </TabGroup>
        <Spacer />
        {tab === "tracks" && <Dropdown label="Машина" value={car} options={carOptions} onChange={setCar} />}
        <Dropdown label="Симулятор" value={simulatorId} options={simulatorOptions} onChange={setSimulator} />
      </FilterBar>

      {tab === "drivers" && (
        <div>
          <SectionHeader $first>
            <SectionTitle>Чемпіони ліги</SectionTitle>
            <SectionNote>Пілоти, що вигравали особистий залік сезону</SectionNote>
          </SectionHeader>
          {errors.champions ? (
            <LoadFailure onRetry={() => loadChampions(simulatorId)} />
          ) : (
            <ChampionsSection
              champions={champions}
              loading={championsLoading}
              onSelectDriver={handleSelectDriver}
            />
          )}

          <SectionHeader>
            <SectionTitle>Статистика за всю історію</SectionTitle>
            <SectionNote>{allSeasonsNote}</SectionNote>
          </SectionHeader>
          {errors["boards:DRIVERS"] ? (
            <LoadFailure onRetry={() => loadBoards("DRIVERS", simulatorId)} />
          ) : (
            <Grid $min="376px" $gap="12px">
              {driverBoardsLoading
                ? Array.from({ length: DRIVER_BOARD_COUNT }, (_, index) => (
                    <BoardTileSkeleton key={index} />
                  ))
                : driverBoards.map((board) => (
                    <BoardTile
                      key={board.kind}
                      board={board}
                      onOpenFull={setModalKind}
                      onSelectDriver={handleSelectDriver}
                    />
                  ))}
            </Grid>
          )}
        </div>
      )}

      {tab === "teams" && (
        <div>
          <SectionHeader $first>
            <SectionTitle>Команди</SectionTitle>
            <SectionNote>{allSeasonsNote}</SectionNote>
          </SectionHeader>
          {errors["boards:TEAMS"] ? (
            <LoadFailure onRetry={() => loadBoards("TEAMS", simulatorId)} />
          ) : (
            <Grid $min="376px" $gap="12px">
              {teamBoardsLoading
                ? Array.from({ length: TEAM_BOARD_COUNT }, (_, index) => <BoardTileSkeleton key={index} />)
                : teamBoards.map((board) => (
                    <BoardTile
                      key={board.kind}
                      board={board}
                      onOpenFull={setModalKind}
                      onSelectDriver={handleSelectDriver}
                    />
                  ))}
            </Grid>
          )}
        </div>
      )}

      {tab === "tracks" && (
        <TracksTab
          tracks={state.tracks}
          failed={Boolean(errors.tracks)}
          onRetry={() => loadTracks(simulatorId)}
          simulatorName={simulatorName}
          onSelectDriver={handleSelectDriver}
          car={car}
          openTrackId={openTrackId}
          onOpenTrack={setOpenTrackId}
          onCloseTrack={() => setOpenTrackId(null)}
        />
      )}

      <PageNote>
        Спеціальні події не входять в офіційну статистику ліги. Результати спеціальних подій не враховані ні в чемпіонських
        титулах, ні в рекордах пілотів і команд, ні в найшвидших колах треків.
      </PageNote>

      <BoardModal
        kind={modalKind}
        simulatorId={simulatorId}
        onClose={() => setModalKind(null)}
        loadFullBoard={loadFullBoard}
        onSelectDriver={handleSelectDriver}
      />
    </PageWrap>
  );
}
